// Polls store + host-authoritative coordinator (Meeting Tools). Lives OUTSIDE
// the React tree (§9.3.1); the Polls panel is the only subscriber, so a poll
// update never re-renders a video tile. Poll traffic is human-frequency and the
// host coalesces result broadcasts, so this stays off the hot path entirely.
//
// The host is the tally authority: it holds the raw votes (per-identity, only to
// dedupe — one vote per participant, keyed by the SFU-signed sender), computes
// aggregate counts, and broadcasts counts ONLY (and only when results are
// shared). No per-voter data ever leaves the host — that is the "soft
// anonymity" we agreed. Everything is ephemeral; results can be exported to CSV.

import type { AttendeeSnapshot, PollDef, PollOption, PollWire } from '@brizina/call-engine';
import { randomPollId } from '@brizina/call-engine';
import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';

import { engine } from '../engine';
import { callStore, pushToast } from './callStore';

export interface PollView {
  id: string;
  title: string;
  options: PollOption[];
  anonymous: boolean;
  showResults: boolean;
  state: 'draft' | 'live' | 'closed';
  /** Host: true tally. Attendee: only populated while results are shared. */
  counts: Record<string, number>;
  total: number;
  /** The option this client voted for (optimistic), or null. */
  myVote: string | null;
  /** Who runs this poll — the host identity to address votes to (self if mine). */
  hostIdentity: string;
}

interface PollStoreState {
  polls: PollView[];
}

export const pollStore = createStore<PollStoreState>(() => ({ polls: [] }));

export function usePollStore<T>(selector: (s: PollStoreState) => T): T {
  return useStore(pollStore, selector);
}

// ── Host-only, non-reactive internals ──────────────────────────────────────
/** pollId → (voterIdentity → optionId). Raw votes, held only to dedupe/tally. */
const hostVotes = new Map<string, Map<string, string>>();
/** Coalesce result broadcasts: at most ~1 per poll per 300ms under a vote burst. */
const broadcastTimers = new Map<string, ReturnType<typeof setTimeout>>();
/** Identities we've already re-synced active polls to (late-join). */
const synced = new Set<string>();

function optionId(): string {
  return randomPollId().slice(0, 6);
}
function getPoll(id: string): PollView | undefined {
  return pollStore.getState().polls.find((p) => p.id === id);
}
function setPolls(fn: (polls: PollView[]) => PollView[]): void {
  pollStore.setState({ polls: fn(pollStore.getState().polls) });
}
function patch(id: string, p: Partial<PollView>): void {
  setPolls((polls) => polls.map((x) => (x.id === id ? { ...x, ...p } : x)));
}

function toDef(p: PollView): PollDef {
  return {
    id: p.id,
    title: p.title,
    options: p.options,
    anonymous: p.anonymous,
    showResults: p.showResults,
    state: p.state === 'closed' ? 'closed' : 'live',
  };
}

/** Host → everyone (or one late-joiner). Counts cross the wire ONLY when shared. */
function broadcastState(id: string, to?: string): void {
  const p = getPoll(id);
  if (!p || p.state === 'draft') return;
  const wire: PollWire = p.showResults
    ? { t: 'state', poll: toDef(p), counts: p.counts, total: p.total }
    : { t: 'state', poll: toDef(p) };
  engine.sendPoll(wire, to);
}

/** Trailing throttle so a burst of incoming votes yields one broadcast. */
function scheduleBroadcast(id: string): void {
  if (broadcastTimers.has(id)) return;
  broadcastTimers.set(
    id,
    setTimeout(() => {
      broadcastTimers.delete(id);
      broadcastState(id);
    }, 300),
  );
}

function tally(id: string): { counts: Record<string, number>; total: number } {
  const p = getPoll(id);
  const votes = hostVotes.get(id) ?? new Map<string, string>();
  const counts: Record<string, number> = {};
  if (p) for (const o of p.options) counts[o.id] = 0;
  for (const opt of votes.values()) counts[opt] = (counts[opt] ?? 0) + 1;
  return { counts, total: votes.size };
}

// ── Host actions ────────────────────────────────────────────────────────────
export function createDraft(): string {
  const self = callStore.getState().localIdentity ?? '';
  const id = randomPollId();
  setPolls((polls) => [
    ...polls,
    {
      id,
      title: '',
      options: [
        { id: optionId(), label: '' },
        { id: optionId(), label: '' },
      ],
      anonymous: false,
      showResults: false,
      state: 'draft',
      counts: {},
      total: 0,
      myVote: null,
      hostIdentity: self,
    },
  ]);
  return id;
}

export function updateDraft(id: string, p: Partial<Pick<PollView, 'title' | 'anonymous'>>): void {
  patch(id, p);
}
export function setOptionLabel(id: string, optId: string, label: string): void {
  setPolls((polls) =>
    polls.map((p) =>
      p.id === id ? { ...p, options: p.options.map((o) => (o.id === optId ? { ...o, label } : o)) } : p,
    ),
  );
}
export function addOption(id: string): void {
  setPolls((polls) =>
    polls.map((p) =>
      p.id === id && p.options.length < 10
        ? { ...p, options: [...p.options, { id: optionId(), label: '' }] }
        : p,
    ),
  );
}
export function removeOption(id: string, optId: string): void {
  setPolls((polls) =>
    polls.map((p) =>
      p.id === id && p.options.length > 2
        ? { ...p, options: p.options.filter((o) => o.id !== optId) }
        : p,
    ),
  );
}

export function launchPoll(id: string): void {
  const p = getPoll(id);
  if (!p) return;
  const options = p.options.filter((o) => o.label.trim() !== '');
  if (p.title.trim() === '' || options.length < 2) {
    pushToast('Add a question and at least two options', 'warning');
    return;
  }
  hostVotes.set(id, new Map());
  patch(id, {
    options,
    state: 'live',
    counts: Object.fromEntries(options.map((o) => [o.id, 0])),
    total: 0,
    myVote: null,
  });
  broadcastState(id);
}
export function endPoll(id: string): void {
  patch(id, { state: 'closed' });
  broadcastState(id);
}
export function removePoll(id: string): void {
  const timer = broadcastTimers.get(id);
  if (timer) {
    clearTimeout(timer);
    broadcastTimers.delete(id);
  }
  hostVotes.delete(id);
  const wasShared = getPoll(id) && getPoll(id)!.state !== 'draft';
  setPolls((polls) => polls.filter((p) => p.id !== id));
  if (wasShared) engine.sendPoll({ t: 'remove', pollId: id });
}
export function setShowResults(id: string, on: boolean): void {
  patch(id, { showResults: on });
  broadcastState(id);
}

// ── Vote (attendee or host-self) ─────────────────────────────────────────────
export function vote(id: string, optId: string): void {
  const p = getPoll(id);
  if (!p || p.state !== 'live') return;
  if (callStore.getState().isHost) {
    const self = callStore.getState().localIdentity ?? 'host';
    let m = hostVotes.get(id);
    if (!m) {
      m = new Map();
      hostVotes.set(id, m);
    }
    m.set(self, optId);
    const { counts, total } = tally(id);
    patch(id, { counts, total, myVote: optId });
    scheduleBroadcast(id);
  } else {
    patch(id, { myVote: optId });
    engine.sendPoll({ t: 'vote', pollId: id, optionId: optId }, p.hostIdentity || undefined);
  }
}

// ── CSV export (ephemeral results) ───────────────────────────────────────────
export function exportPollCsv(id: string): void {
  const p = getPoll(id);
  if (!p) return;
  const esc = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  const rows = [
    ['Option', 'Votes'],
    ...p.options.map((o) => [o.label, String(p.counts[o.id] ?? 0)]),
  ];
  const csv = rows.map((r) => r.map(esc).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `poll-${p.title.replace(/[^a-z0-9]+/gi, '-').slice(0, 40) || 'results'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Incoming messages (engine → store) ───────────────────────────────────────
function handlePoll(from: string, wire: PollWire): void {
  if (wire.t === 'vote') {
    // Host is the only tally authority.
    if (!callStore.getState().isHost) return;
    const p = getPoll(wire.pollId);
    if (!p || p.state !== 'live' || !p.options.some((o) => o.id === wire.optionId)) return;
    let m = hostVotes.get(wire.pollId);
    if (!m) {
      m = new Map();
      hostVotes.set(wire.pollId, m);
    }
    m.set(from, wire.optionId); // one vote per identity — last write wins
    const { counts, total } = tally(wire.pollId);
    patch(wire.pollId, { counts, total });
    scheduleBroadcast(wire.pollId);
    return;
  }
  if (wire.t === 'remove') {
    setPolls((polls) => polls.filter((p) => p.id !== wire.pollId));
    return;
  }
  // 'state' — attendee side (the host never receives its own broadcasts).
  const existing = getPoll(wire.poll.id);
  const view: PollView = {
    id: wire.poll.id,
    title: wire.poll.title,
    options: wire.poll.options,
    anonymous: wire.poll.anonymous,
    showResults: wire.poll.showResults,
    state: wire.poll.state,
    counts: wire.counts ?? (wire.poll.showResults ? (existing?.counts ?? {}) : {}),
    total: wire.total ?? (wire.poll.showResults ? (existing?.total ?? 0) : 0),
    myVote: existing?.myVote ?? null,
    hostIdentity: from,
  };
  setPolls((polls) => (existing ? polls.map((p) => (p.id === view.id ? view : p)) : [...polls, view]));
  if (!existing && view.state === 'live') pushToast('A new poll has been launched');
}

/** Late-join: when a new participant appears, the host re-sends active polls. */
function onAttendee(snap: AttendeeSnapshot): void {
  if (snap.isLocal || synced.has(snap.identity)) return;
  synced.add(snap.identity);
  if (!callStore.getState().isHost) return;
  for (const p of pollStore.getState().polls) {
    if (p.state !== 'draft') broadcastState(p.id, snap.identity);
  }
}

export function resetPolls(): void {
  for (const t of broadcastTimers.values()) clearTimeout(t);
  broadcastTimers.clear();
  hostVotes.clear();
  synced.clear();
  pollStore.setState({ polls: [] });
}

// Wire once on import (this module is loaded for every call via MeetingPage).
engine.events.on('poll', handlePoll);
engine.events.on('attendeeUpserted', onAttendee);
